/**
 * Who is rendering the shared chrome: the shell, or the page?
 *
 * Story #5343 (Launch W1). The four shared-chrome components move into
 * Layout.tsx so all 143 screens get them at once. That creates one problem:
 * many pages already render their own copy of a chrome element, and two of
 * everything is worse than one of nothing.
 *
 * Measured on 2026-09-02, before this change:
 *   - 24 pages render their own <ScopeRibbon /> AND Layout renders one. The
 *     W1_SHELL flag went true in production earlier the same day, so those 24
 *     screens have been showing two scope bars ever since.
 *   - 18 pages mount their own <HelpDrawer />, as does Layout.
 *   - 19 pages mount a per-domain tab strip (CustomersNav, KnowledgeNav,
 *     AdministrationNav, OperationsNav) that DomainTabs now supersedes.
 *
 * Editing 61 page files to delete those mounts would be 61 chances to miss one
 * and 61 merge conflicts against in-flight wave branches. Instead the shell
 * announces what it has already drawn, and a page-level copy renders null.
 *
 * The default is all-false, so a component used outside the shell -- in a test,
 * in Storybook, on a route with no Layout -- behaves exactly as it did before.
 */
import React, { createContext, useContext, useMemo } from 'react';

export interface ShellChromeState {
  /** The shell rendered the G01 scope ribbon. */
  scopeRibbon: boolean;
  /** The shell owns the G07 contextual-help drawer and its open state. */
  help: boolean;
  /** The shell rendered the per-domain tab strip for this route. */
  tabs: boolean;
  /** The shell rendered the G02 breadcrumb, title and description. */
  header: boolean;
}

const NOTHING_RENDERED: ShellChromeState = {
  scopeRibbon: false,
  help: false,
  tabs: false,
  header: false,
};

const ShellChromeContext = createContext<ShellChromeState>(NOTHING_RENDERED);

export const ShellChromeProvider: React.FC<{
  value: ShellChromeState;
  children: React.ReactNode;
}> = ({ value, children }) => {
  const memo = useMemo(
    () => value,
    [value.scopeRibbon, value.help, value.tabs, value.header],
  );
  return <ShellChromeContext.Provider value={memo}>{children}</ShellChromeContext.Provider>;
};

/** What the shell has already drawn around this page. */
export function useShellChrome(): ShellChromeState {
  return useContext(ShellChromeContext);
}

/**
 * True when a page-level copy of `element` should render nothing because the
 * shell has already drawn it.
 *
 * Call it from the component itself, not from every page:
 *
 *   const ScopeRibbon = ({ shell = false }) => {
 *     if (useShellOwns('scopeRibbon', shell)) return null;
 *     ...
 *   };
 *
 * The shell passes `shell` so its own instance always renders.
 */
export function useShellOwns(element: keyof ShellChromeState, isShellInstance = false): boolean {
  const state = useShellChrome();
  return !isShellInstance && state[element];
}
